import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * SearchableSelect — combobox with type-ahead filtering and inline creation.
 *
 * Props:
 *   options     - Array of { id, name, category? }
 *   value       - Selected option id (or null)
 *   onChange    - (id, name) => void
 *   onCreateNew - (name) => Promise<{ id, name }> — called when user creates a new entry
 *   placeholder - input placeholder text
 *   groupBy     - optional key to group options (e.g., 'category')
 *   disabled    - disable the input
 *   className   - additional CSS classes for the wrapper
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  onCreateNew,
  placeholder = 'Search or type to create...',
  groupBy,
  disabled = false,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Find the selected option's display name
  const selectedOption = options.find(o => o.id === value);

  // Filter options by search text (case-insensitive substring)
  const filtered = search.trim()
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Check if exact match exists (case-insensitive)
  const exactMatch = options.some(o => o.name.toLowerCase() === search.trim().toLowerCase());
  const showCreateOption = search.trim() && !exactMatch && onCreateNew;

  // Build flat list for keyboard nav (grouped items flattened)
  const flatItems = [];
  if (groupBy) {
    const groups = {};
    for (const opt of filtered) {
      const group = opt[groupBy] || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(opt);
    }
    for (const [, items] of Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))) {
      for (const item of items) {
        flatItems.push(item);
      }
    }
  } else {
    flatItems.push(...filtered);
  }

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        // Reset search to selected name if closing without selection
        if (selectedOption) {
          setSearch('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightIndex >= 0) {
      const items = listRef.current.querySelectorAll('[data-selectable]');
      if (items[highlightIndex]) {
        items[highlightIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex]);

  const totalSelectableItems = flatItems.length + (showCreateOption ? 1 : 0);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(prev =>
          prev < totalSelectableItems - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(prev =>
          prev > 0 ? prev - 1 : totalSelectableItems - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < flatItems.length) {
          selectOption(flatItems[highlightIndex]);
        } else if (highlightIndex === flatItems.length && showCreateOption) {
          handleCreate();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearch('');
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  }, [isOpen, highlightIndex, flatItems, totalSelectableItems, showCreateOption, search]);

  const selectOption = (opt) => {
    onChange(opt.id, opt.name);
    setSearch('');
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleCreate = async () => {
    if (!onCreateNew || !search.trim() || creating) return;
    setCreating(true);
    try {
      const created = await onCreateNew(search.trim());
      if (created) {
        onChange(created.id, created.name);
        setSearch('');
        setIsOpen(false);
      }
    } catch (err) {
      console.error('Failed to create:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    setHighlightIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearch('');
  };

  // Group options for rendering
  const renderOptions = () => {
    if (filtered.length === 0 && !showCreateOption) {
      return (
        <div className="px-3 py-2 text-sm text-gray-600">
          {search.trim() ? 'No matches found' : 'No options available'}
        </div>
      );
    }

    const items = [];
    let selectableIndex = 0;

    if (groupBy) {
      const groups = {};
      for (const opt of filtered) {
        const group = opt[groupBy] || 'Other';
        if (!groups[group]) groups[group] = [];
        groups[group].push(opt);
      }

      const sortedGroups = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
      for (const [groupName, groupItems] of sortedGroups) {
        items.push(
          <div key={`group-${groupName}`} className="px-3 py-1 text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-50">
            {groupName}
          </div>
        );
        for (const opt of groupItems) {
          const idx = selectableIndex++;
          items.push(
            <button
              key={opt.id}
              data-selectable
              type="button"
              className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors ${
                opt.id === value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
              } ${idx === highlightIndex ? 'bg-primary-100' : 'hover:bg-gray-50'}`}
              onMouseEnter={() => setHighlightIndex(idx)}
              onClick={() => selectOption(opt)}
            >
              {opt.name}
            </button>
          );
        }
      }
    } else {
      for (const opt of filtered) {
        const idx = selectableIndex++;
        items.push(
          <button
            key={opt.id}
            data-selectable
            type="button"
            className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors ${
              opt.id === value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
            } ${idx === highlightIndex ? 'bg-primary-100' : 'hover:bg-gray-50'}`}
            onMouseEnter={() => setHighlightIndex(idx)}
            onClick={() => selectOption(opt)}
          >
            {opt.name}
          </button>
        );
      }
    }

    // "Create [typed value]" option at bottom
    if (showCreateOption) {
      const createIdx = selectableIndex;
      items.push(
        <button
          key="__create__"
          data-selectable
          type="button"
          className={`w-full text-left px-3 py-2 text-sm cursor-pointer border-t border-gray-100 transition-colors ${
            createIdx === highlightIndex ? 'bg-primary-100' : 'hover:bg-gray-50'
          }`}
          onMouseEnter={() => setHighlightIndex(createIdx)}
          onClick={handleCreate}
          disabled={creating}
        >
          <span className="text-primary-600 font-medium">
            {creating ? 'Creating...' : `Create "${search.trim()}"`}
          </span>
        </button>
      );
    }

    return items;
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        className="input"
        value={isOpen ? search : (selectedOption?.name || '')}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {/* Clear button */}
      {value && !isOpen && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null, '');
            setSearch('');
          }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {/* Dropdown chevron */}
      {!value && !isOpen && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </div>
      )}
      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {renderOptions()}
        </div>
      )}
    </div>
  );
}
